import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import CheckboxGroup from "@/components/checkbox-group"
import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { handleNetworkError } from "@/services/base"
import useStore from "@/store"
import { LabelValue } from "@/types/common"
import { HTTPError } from "ky"
import { difference } from "lodash"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export interface SelectProcessSheetProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onClickAdd?: (processIds: string[]) => void
}

export default function SelectProcessSheet({
  open,
  onOpenChange,
  onClickAdd,
}: SelectProcessSheetProps) {
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const sessionStore = useStore.use.session()
  const options = useProductOptions()
  const [selected, setSelected] = useState<string[]>(
    sessionStore.session.processes.map((item) => {
      return item.processId
    }) || []
  )
  const [defaultSelected, setDefaultSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useInit()

  function useInit() {
    useEffect(() => {
      const newProcesses = sessionStore.session.processes.map((item) => {
        return item.processId
      })
      setDefaultSelected(newProcesses)
    }, [sessionStore.session.processes])
  }

  function useProductOptions(): LabelValue[] {
    const [options, setOptions] = useState<LabelValue[]>([])

    useEffect(() => {
      if (!userStore.processes.length) {
        setOptions([])

        return
      }

      setOptions(
        userStore.processes.map((item) => ({
          id: item.id,
          label: item.name,
          value: item.id,
        }))
      )
    }, [userStore.processes])

    return options
  }

  function handleOpenChange(open: boolean) {
    if (onOpenChange) {
      onOpenChange(open)
    }
  }

  function handleSelectProcesses(value: string[]) {
    setSelected(value as string[])
  }

  async function handleAddProcesses() {
    try {
      setIsLoading(true)
      const newProcesses = difference(selected, defaultSelected)
      if (!newProcesses.length) {
        handleOpenChange(false)

        return
      }

      setDefaultSelected(selected)
      handleOpenChange(false)

      if (onClickAdd) {
        onClickAdd(newProcesses)
      }
    } catch (err) {
      console.error(err)
      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ShadSheet open={open} onOpenChange={handleOpenChange}>
      <ShadSheetContent
        side="bottom"
        className="shadow-drop-shadow mx-auto max-w-md rounded-t-lg"
      >
        <ShadSheetHeader>
          <ShadSheetTitle className="text-xl">Select Process</ShadSheetTitle>
        </ShadSheetHeader>

        <SheetDescription className="sr-only">
          Select processes to add
        </SheetDescription>

        <div className="flex flex-col justify-center gap-5 pb-8 px-4 min-h-25">
          <CheckboxGroup
            label="Select Processes (Multi select)"
            options={options}
            hideLabel
            defaultValue={defaultSelected}
            disableDefaultSelected
            value={selected}
            onChange={handleSelectProcesses}
          />
        </div>

        <Bottombar className="flex gap-4 bg-white">
          <Button variant="outline" onClick={close}>
            Back
          </Button>
          <Button
            variant="default"
            className="flex-1"
            isLoading={isLoading}
            onClick={handleAddProcesses}
          >
            Add Processes
          </Button>
        </Bottombar>
      </ShadSheetContent>
    </ShadSheet>
  )
}
